import type { ScanRecord } from '../services/scanService';

export interface FoodToReduce {
  name: string;
  description: string;
  icon: 'cake' | 'milk' | 'fries' | 'bread' | 'soda' | 'chocolate' | 'alcohol' | 'fastfood';
}

/**
 * Generates personalized foods to reduce based on user's latest scan
 */
export function generateFoodsToReduce(
  latestScan: ScanRecord | null,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian'
): FoodToReduce[] {
  const foods: FoodToReduce[] = [];
  
  if (!latestScan?.analysis) {
    // Default foods if no scan available
    foods.push({
      name: 'High-Sugar Foods',
      description: 'Candy, soda, pastries — can spike insulin and trigger inflammation',
      icon: 'cake'
    });
    foods.push({
      name: 'Dairy Products',
      description: 'Milk, cheese (for some) — may increase sebum production and breakouts',
      icon: 'milk'
    });
    foods.push({
      name: 'Fried & Processed Foods',
      description: 'Fast food, chips — high in unhealthy fats that promote inflammation',
      icon: 'fries'
    });
    foods.push({
      name: 'Refined Carbs',
      description: 'White bread, pasta — high glycemic index can worsen acne',
      icon: 'bread'
    });
    return foods;
  }

  const analysis = latestScan.analysis;
  const glowScore = analysis.skinHealthIndex ?? latestScan.glowScore ?? 70;
  const acneSeverity = (analysis.acneAnalysis?.overallSeverity ?? analysis.acneSeverity ?? 'mild') as string;
  const estimatedPH = analysis.estimatedPH ?? analysis.acneAnalysis?.estimatedPH;
  const isInflamed = analysis.acneAnalysis?.acneType?.inflammatory === 'moderate' || 
                     analysis.acneAnalysis?.acneType?.inflammatory === 'pronounced' ||
                     acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe';
  const isHighPH = (estimatedPH ?? 5.0) > 5.5;
  const isLowPH = (estimatedPH ?? 5.0) < 4.8;

  // Use a seed based on scan parameters to ensure variety
  // Include scanId or timestamp to ensure different scans produce different results
  const scanIdHash = latestScan?.scanId ? latestScan.scanId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) : 0;
  const timestampHash = latestScan?.timestamp ? new Date(latestScan.timestamp).getTime() % 1000 : 0;
  const seed = Math.floor((glowScore * 150 + (estimatedPH || 5.0) * 20 + 
    (acneSeverity === 'severe' ? 150 : acneSeverity === 'moderate' ? 75 : 0) + scanIdHash + timestampHash) % 20000);

  // Pool of all possible foods to reduce
  const allFoods: FoodToReduce[] = [];

  // High-sugar foods (important for all, especially low scores)
  if (glowScore < 70 || acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    allFoods.push({
      name: 'High-Sugar Foods',
      description: 'Candy, soda, pastries — can spike insulin and trigger inflammation',
      icon: 'cake'
    });
    allFoods.push({
      name: 'Sugary Drinks',
      description: 'Soda, energy drinks, sweetened beverages — spike blood sugar and worsen acne',
      icon: 'soda'
    });
    allFoods.push({
      name: 'Desserts & Sweets',
      description: 'Cakes, cookies, ice cream — high sugar content triggers breakouts',
      icon: 'cake'
    });
  } else {
    allFoods.push({
      name: 'Excessive Sugar',
      description: 'Limit candy, soda, pastries — can spike insulin and trigger inflammation',
      icon: 'cake'
    });
  }

  // Dairy products (especially important for inflamed acne)
  if (isInflamed || acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    allFoods.push({
      name: 'Dairy Products',
      description: 'Milk, cheese, ice cream — may increase sebum production and breakouts',
      icon: 'milk'
    });
    allFoods.push({
      name: 'High-Dairy Foods',
      description: 'Cheese, cream, butter — can trigger inflammation in sensitive skin',
      icon: 'milk'
    });
  } else {
    allFoods.push({
      name: 'Excessive Dairy',
      description: 'Milk, cheese (for some) — may increase sebum production and breakouts',
      icon: 'milk'
    });
  }

  // Fried and processed foods
  if (glowScore < 60 || isInflamed) {
    allFoods.push({
      name: 'Fried & Processed Foods',
      description: 'Fast food, chips, fried snacks — high in unhealthy fats that promote inflammation',
      icon: 'fries'
    });
    allFoods.push({
      name: 'Fast Food',
      description: 'Burgers, fries, fried chicken — trans fats and processed ingredients worsen skin',
      icon: 'fastfood'
    });
    allFoods.push({
      name: 'Processed Snacks',
      description: 'Chips, crackers, packaged snacks — contain additives that can trigger breakouts',
      icon: 'fries'
    });
  } else {
    allFoods.push({
      name: 'Fried Foods',
      description: 'Fast food, chips — high in unhealthy fats that promote inflammation',
      icon: 'fries'
    });
  }

  // Refined carbs (especially for low scores)
  if (glowScore < 70) {
    allFoods.push({
      name: 'Refined Carbs',
      description: 'White bread, pasta, white rice — high glycemic index can worsen acne',
      icon: 'bread'
    });
    allFoods.push({
      name: 'White Flour Products',
      description: 'White bread, pastries, crackers — spike blood sugar and trigger breakouts',
      icon: 'bread'
    });
  } else {
    allFoods.push({
      name: 'Excessive Refined Carbs',
      description: 'White bread, pasta — high glycemic index can worsen acne',
      icon: 'bread'
    });
  }

  // Chocolate (especially milk chocolate)
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    allFoods.push({
      name: 'Milk Chocolate',
      description: 'High-sugar chocolate — dairy and sugar combination triggers breakouts',
      icon: 'chocolate'
    });
  } else {
    allFoods.push({
      name: 'High-Sugar Chocolate',
      description: 'Milk chocolate, candy bars — high sugar and dairy content can trigger breakouts',
      icon: 'chocolate'
    });
  }

  // Alcohol (especially for low scores or high pH)
  if (glowScore < 60 || isHighPH) {
    allFoods.push({
      name: 'Alcohol',
      description: 'Especially sugary cocktails — dehydrates skin and disrupts hormone balance',
      icon: 'alcohol'
    });
  }

  // High-sodium foods (for high pH or inflammation)
  if (isHighPH || isInflamed) {
    allFoods.push({
      name: 'High-Sodium Foods',
      description: 'Processed meats, canned foods — can cause water retention and inflammation',
      icon: 'fastfood'
    });
  }

  // Whey protein (for vegetarians, show plant-based warning)
  if (acneSeverity === 'moderate' || acneSeverity === 'moderate_to_severe' || acneSeverity === 'severe') {
    if (dietaryPreference !== 'vegetarian') {
      allFoods.push({
        name: 'Whey Protein Supplements',
        description: 'Dairy-derived protein — may trigger acne in some individuals',
        icon: 'milk'
      });
    }
  }

  // Simple seeded shuffle function
  const seededShuffle = <T,>(array: T[], seed: number): T[] => {
    const shuffled = [...array];
    let currentSeed = seed;
    for (let i = shuffled.length - 1; i > 0; i--) {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = Math.floor((currentSeed / 233280) * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Shuffle and select 4 foods
  const shuffled = seededShuffle(allFoods, seed);
  return shuffled.slice(0, 4);
}


export interface FoodToReduce {
  name: string;
  description: string;
  icon: 'cake' | 'milk' | 'fries' | 'bread' | 'soda' | 'chocolate' | 'alcohol' | 'fastfood';
}

